'use client';

import { useLocale, useTranslations } from 'next-intl';
import { Check, Pencil } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { formatDate, formatEstimateMoney } from '@/shared/lib/utils';
import type { EstimateSummary } from '@/features/design/estimate';

interface AlbumEstimateOptionsProps {
  /** Ready estimates of THIS design — listed first (default selection). */
  currentDesignEstimates: EstimateSummary[];
  /** Ready estimates of sibling designs — greyed at the end, explicitly
   *  marked with the owning design's number. */
  otherDesignEstimates: EstimateSummary[];
  /** Selected ready estimate id, or null = "New estimate". Only meaningful
   *  while `isEstimateIncluded` is true. */
  selectedEstimateId: string | null;
  /** False = the "Don't include" option is selected. */
  isEstimateIncluded: boolean;
  onSelect: (estimateId: string | null) => void;
  /** "Don't include" — the album skips the cost sections entirely. */
  onExclude: () => void;
  disabled?: boolean;
}

/**
 * Option list of the album Estimate picker — shared by the single-column
 * NestedSheet (`AlbumPanelSettings`) and the two-column drill-down view
 * (`AlbumTabContent`). Order: this design's estimates, "New estimate", then
 * sibling-design estimates greyed at the end so the user clearly sees whether
 * THIS design has its own estimate.
 */
export function AlbumEstimateOptions({
  currentDesignEstimates,
  otherDesignEstimates,
  selectedEstimateId,
  isEstimateIncluded,
  onSelect,
  onExclude,
  disabled = false,
}: AlbumEstimateOptionsProps) {
  const tEstimate = useTranslations('common.estimate');
  const locale = useLocale();

  const optionButton = (estimate: EstimateSummary, isForeign: boolean) => {
    const isSelected = isEstimateIncluded && estimate.id === selectedEstimateId;
    const title = tEstimate('card.defaultTitle', {
      date: formatDate(estimate.created_at, locale),
    });
    return (
      <button
        key={estimate.id}
        type="button"
        onClick={() => onSelect(estimate.id)}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between gap-2 p-4 rounded-lg border-2 transition-colors text-start',
          isSelected ? 'border-primary bg-primary/5' : 'border-border',
          !disabled && 'hover:bg-muted/50',
          // Sibling-design estimates read muted ("grey") but stay selectable.
          isForeign && !isSelected && 'opacity-60'
        )}
      >
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-list-item-subtitle truncate">{title}</span>
          <span className="text-card-meta text-muted-foreground tabular-nums">
            {formatEstimateMoney(estimate.grand_total, estimate.currency)}
          </span>
          {/* Explicit origin mark so a sibling-design estimate is never
              mistaken for this design's own. */}
          {isForeign && (
            <span className="text-card-meta text-muted-foreground">
              {tEstimate('album.fromDesign', {
                number: estimate.conceptSequenceNumber ?? '—',
              })}
            </span>
          )}
          {/* Edited estimate → embedded as-is (no regeneration). */}
          {estimate.is_edited && (
            <span className="inline-flex items-center gap-1 text-card-meta text-muted-foreground">
              <Pencil className="h-3 w-3 flex-shrink-0" strokeWidth={1.5} />
              {tEstimate('album.editedUsedAsIs')}
            </span>
          )}
        </div>
        {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
      </button>
    );
  };

  const isNewSelected = isEstimateIncluded && selectedEstimateId === null;

  return (
    <div className="space-y-2">
      {/* Don't include — the album skips the cost sections entirely. Listed
          first, like other "None" options. */}
      <button
        type="button"
        onClick={onExclude}
        disabled={disabled}
        className={cn(
          'w-full flex flex-col gap-0.5 p-4 rounded-lg border-2 transition-colors text-start',
          !isEstimateIncluded ? 'border-primary bg-primary/5' : 'border-border',
          !disabled && 'hover:bg-muted/50'
        )}
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-list-item-subtitle">
            {tEstimate('album.dontInclude')}
          </span>
          {!isEstimateIncluded && (
            <Check className="h-4 w-4 text-primary flex-shrink-0" />
          )}
        </div>
        <span className="text-card-meta text-muted-foreground">
          {tEstimate('album.dontIncludeDescription')}
        </span>
      </button>

      {/* This design's ready estimates — the default lives here. */}
      {currentDesignEstimates.map((estimate) => optionButton(estimate, false))}

      {/* New estimate — the server generates a fresh one for this design
          inside the album pipeline and embeds it. */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        disabled={disabled}
        className={cn(
          'w-full flex flex-col gap-0.5 p-4 rounded-lg border-2 transition-colors text-start',
          isNewSelected ? 'border-primary bg-primary/5' : 'border-border',
          !disabled && 'hover:bg-muted/50'
        )}
      >
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-list-item-subtitle">
            {tEstimate('album.generateNew')}
          </span>
          {isNewSelected && (
            <Check className="h-4 w-4 text-primary flex-shrink-0" />
          )}
        </div>
        <span className="text-card-meta text-muted-foreground">
          {tEstimate('album.generateNewDescription')}
        </span>
      </button>

      {/* Sibling designs' estimates — greyed tail, explicitly marked. */}
      {otherDesignEstimates.length > 0 && (
        <>
          <div className="text-card-meta text-muted-foreground pt-2 px-1">
            {tEstimate('album.fromOtherDesigns')}
          </div>
          {otherDesignEstimates.map((estimate) => optionButton(estimate, true))}
        </>
      )}
    </div>
  );
}
